import { Injectable, inject, signal, computed } from '@angular/core';
import { Subscription } from 'rxjs';
import { ProjectService } from '../project/project.service';
import { NotificationWebsocketService } from '../notification-websocket/notification-websocket.service';
import { AppEventBus, AppEvent, assertNever } from '../events/app-events';
import { DataChangeType } from '../../interfaces/rt-messaging.generated';

// Runtime mirror of the `DataChangeType` string-literal union. `satisfies`
// makes adding a variant to `DataChangeType` (e.g. SCRUM-751 content events)
// a compile error here until the runtime set is updated — preserving the
// "no silent drop" guarantee at the unvalidated websocket boundary.
const KNOWN_DATA_CHANGE_TYPES = {
  versions: true,
  workflows: true,
  project: true,
  // SCRUM-751 content edits
  project_files: true,
  project_constants: true,
  workflow_steps: true,
  workflow_default_files: true,
  workflow_variable_schema: true,
} as const satisfies Record<DataChangeType, true>;

function isDataChangeType(value: unknown): value is DataChangeType {
  return (
    typeof value === 'string' &&
    Object.prototype.hasOwnProperty.call(KNOWN_DATA_CHANGE_TYPES, value)
  );
}

@Injectable({
  providedIn: 'root',
})
export class ProjectWatchService {
  private readonly projectService = inject(ProjectService);
  private readonly notificationWebsocketService = inject(NotificationWebsocketService);
  private readonly appEventBus = inject(AppEventBus);

  readonly #watchState = signal<boolean>(false);
  readonly isWatching = computed(() => this.#watchState());

  private messageSubscription?: Subscription;
  private currentProjectId: number | null = null;

  /**
   * Initialize watch service for a project.
   * Fetches initial watch status and subscribes to live data changes.
   */
  initialize(projectId: number): void {
    this.cleanup();
    this.currentProjectId = projectId;

    // Fetch initial watch status from REST API
    this.projectService.getWatchStatus(projectId).subscribe({
      next: (status) => {
        this.#watchState.set(status.watching);
      },
      error: (error) => {
        console.warn('Failed to get watch status:', error);
      },
    });

    // Subscribe to live data changes via WebSocket
    this.notificationWebsocketService.watchProjectData(projectId).catch((error) => {
      console.warn('Failed to subscribe to project data changes:', error);
    });

    // Listen for data-changed messages and dispatch to appropriate refresh mechanisms
    this.messageSubscription = this.notificationWebsocketService
      .receiveMessage()
      .subscribe((data) => {
        if (data.message === 'data-changed' && 'project_id' in data && 'change_type' in data) {
          const projectId = data['project_id'] as number;
          if (projectId !== this.currentProjectId) return;
          // Validate the change_type at the unvalidated websocket boundary. An
          // out-of-sync server (or future variant not yet handled here) must
          // not throw from inside the subscription via `assertNever`.
          const changeType = data['change_type'];
          if (!isDataChangeType(changeType)) {
            console.warn('Unknown data-changed change_type, ignoring:', changeType);
            return;
          }
          // context_id carries the workflow id for workflow-scoped change types
          // (SCRUM-751); absent/null for project-scoped changes.
          const rawContextId = 'context_id' in data ? data['context_id'] : null;
          const contextId = typeof rawContextId === 'number' ? rawContextId : null;
          this.handleDataChanged(changeType, contextId);
        }
      });
  }

  /**
   * Cleanup when leaving a project.
   */
  cleanup(): void {
    if (this.messageSubscription) {
      this.messageSubscription.unsubscribe();
      this.messageSubscription = undefined;
    }

    if (this.currentProjectId !== null) {
      this.notificationWebsocketService.unwatchProjectData(this.currentProjectId).catch((error) => {
        console.warn('Failed to unsubscribe from project data changes:', error);
      });
    }

    this.currentProjectId = null;
    this.#watchState.set(false);
  }

  /**
   * Toggle the REST watch status (notification subscription).
   */
  toggleWatch(projectId: number): void {
    if (this.#watchState()) {
      this.projectService.unwatchProject(projectId).subscribe({
        next: () => this.#watchState.set(false),
        error: (error) => console.error('Failed to unwatch project:', error),
      });
    } else {
      this.projectService.watchProject(projectId).subscribe({
        next: () => this.#watchState.set(true),
        error: (error) => console.error('Failed to watch project:', error),
      });
    }
  }

  private handleDataChanged(changeType: DataChangeType, contextId: number | null): void {
    if (this.currentProjectId === null) return;
    const event = this.toEvent(changeType, contextId);
    if (event !== null) {
      this.appEventBus.emit(event);
    }
  }

  /**
   * Map a server data-change type to a typed AppEvent. The exhaustive switch
   * (assertNever) makes it a compile error if the generated `DataChangeType`
   * union gains a variant that isn't routed here (e.g. the SCRUM-751 content
   * change types) — so a new server signal can never be silently dropped.
   *
   * Workflow-scoped types (steps, default files, variable schema) require a
   * numeric `context_id` (the workflow id). A malformed signal missing it is
   * dropped (returns null) rather than emitted with an invalid workflowId.
   */
  private toEvent(change: DataChangeType, contextId: number | null): AppEvent | null {
    switch (change) {
      case 'versions':
        // A published version refreshes the header (name + version list).
        return { kind: 'reloadVersions' };
      case 'project':
        // A project name/description change refreshes the header.
        return { kind: 'refreshProject' };
      case 'workflows':
        // A workflow create/delete reloads the workflow dropdown.
        return { kind: 'reloadWorkflows' };
      case 'project_files':
        // A project file was added/changed/removed — reload the file tree.
        return { kind: 'reloadFiles' };
      case 'project_constants':
        // Project constants changed — reload the project-variables view.
        return { kind: 'reloadProjectConstants' };
      case 'workflow_steps':
        return contextId === null ? null : { kind: 'reloadSteps', workflowId: contextId };
      case 'workflow_default_files':
        return contextId === null ? null : { kind: 'reloadWorkflowFiles', workflowId: contextId };
      case 'workflow_variable_schema':
        return contextId === null
          ? null
          : { kind: 'reloadWorkflowVariableSchema', workflowId: contextId };
      default:
        return assertNever(change);
    }
  }
}
